import { useCallback, useRef } from "react";

/**
 * Tracks how much of a video/podcast was ACTUALLY played, and calls back once
 * that crosses `threshold` of the duration.
 *
 * The naive signal -- `currentTime / duration >= 0.9` -- is satisfied by dragging
 * the scrubber to the end, which is not watching a lesson. So we accumulate the
 * forward deltas between `timeupdate` ticks instead: a jump larger than a plausible
 * tick is a seek, and contributes nothing. Playing at 2x still counts fully (the
 * media clock advances in media-time either way), which is fine -- the student did
 * hear the whole thing.
 */

/** `timeupdate` fires ~4x/sec; anything past this gap is a seek, not playback. */
const MAX_PLAUSIBLE_TICK_SECONDS = 2.5;

export function useMediaCompletion(opts: {
  onComplete: () => void;
  /** Fraction of the duration that must be played. Defaults to 0.9. */
  threshold?: number;
}): (info: { currentTime: number; duration: number }) => void {
  const { onComplete, threshold = 0.9 } = opts;
  const playedRef = useRef(0);
  const lastTimeRef = useRef<number | null>(null);
  const doneRef = useRef(false);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  return useCallback(({ currentTime, duration }) => {
    const previous = lastTimeRef.current;
    lastTimeRef.current = currentTime;

    if (previous != null) {
      const delta = currentTime - previous;
      if (delta > 0 && delta <= MAX_PLAUSIBLE_TICK_SECONDS) playedRef.current += delta;
    }

    if (doneRef.current) return;
    if (!Number.isFinite(duration) || duration <= 0) return;
    if (playedRef.current / duration >= threshold) {
      doneRef.current = true;
      onCompleteRef.current();
    }
  }, [threshold]);
}
